import { useState, useEffect } from 'react'
import { Loader } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'
import { alarmApi } from '../lib/alarmApi'
import { DynamicGraphCard } from './DynamicGraphCard'
import { EventMonitorPanel } from './EventMonitorPanel'

const PERF_COLORS = ['#8b5cf6', '#358ECD', '#10b981', '#f59e0b', '#ef4444', '#06b6d4']

/**
 * Dynamic "System Performance" dashboard.
 *  - Graph: shared DynamicGraphCard (D/W/M/Y, dates, 3-dot menu, editor)
 *  - Alarm Summary: real alarms + status filter + edit (→ Alarms manager)
 *  - Logger Diagnostics: latest value of each diagnostic/meta parameter
 */
export function SystemPerformanceView({
  sites = [], selectedParameters = [], dateRange = {}, timezone = 'UTC',
  activePeriod = 'Y', onPeriodClick, onDateChange, projectId,
}) {
  const [alarms, setAlarms] = useState([])
  const [loadingAlarms, setLoadingAlarms] = useState(false)

  const [diag, setDiag] = useState({})
  const [loadingDiag, setLoadingDiag] = useState(false)

  const fetchAlarms = () => {
    if (!projectId) return
    setLoadingAlarms(true)
    alarmApi.getAlarms({ project: projectId })
      .then(res => setAlarms(Array.isArray(res.data) ? res.data : (res.data?.results || [])))
      .catch(() => setAlarms([]))
      .finally(() => setLoadingAlarms(false))
  }

  // Diagnostic/meta parameters (sensor name contains "meta"); else selected params
  const metaParams = []
  sites.forEach(site =>
    (site.devices || []).forEach(dev =>
      (dev.sensors || []).forEach(sensor => {
        if (/meta/i.test(sensor.name || '')) {
          (sensor.parameters || []).forEach(p => metaParams.push({ ...p, siteName: site.name }))
        }
      })
    )
  )
  const diagParams = metaParams.length > 0 ? metaParams : selectedParameters

  // ── Alarms ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchAlarms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  // ── Logger diagnostics (latest value per param) ─────────────────────────
  useEffect(() => {
    if (diagParams.length === 0) { setDiag({}); return }
    let cancelled = false
    setLoadingDiag(true)
    Promise.allSettled(
      diagParams.map(p =>
        projectsApi.fetchGraphData(Number(p.id), { fromDate: dateRange.from, toDate: dateRange.to, timezone })
          .then(res => ({
            id: p.id,
            value: (res && typeof res === 'object' && !res.error) ? res.latestValue : null,
            unit: (res && res.unit) || p.unit || '',
            ts: (res && res.latestTimestamp) || null,
          }))
      )
    ).then(results => {
      if (cancelled) return
      const map = {}
      results.forEach(r => { if (r.status === 'fulfilled' && r.value?.id != null) map[r.value.id] = r.value })
      setDiag(map); setLoadingDiag(false)
    }).catch(() => { if (!cancelled) setLoadingDiag(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(diagParams.map(p => p.id)), dateRange.from, dateRange.to, timezone])

  return (
    <div className="space-y-4">
      {/* ── Graph (shared card with 3-dot + editor) ── */}
      <DynamicGraphCard
        title="Graph"
        selectedParameters={selectedParameters}
        dateRange={dateRange}
        timezone={timezone}
        activePeriod={activePeriod}
        onPeriodClick={onPeriodClick}
        onDateChange={onDateChange}
        storageKey="wq_system_performance_graph"
        colors={PERF_COLORS}
        emptyMessage="Select one or more parameters from the sidebar to view system performance."
      />

      {/* ── Event Monitor + Logger Diagnostics ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Event Monitor (with inline edit mode + settings modal) */}
        <EventMonitorPanel alarms={alarms} loading={loadingAlarms} onChanged={fetchAlarms} sites={sites} projectId={projectId} />

        {/* Logger Diagnostics */}
        <div className="bg-white border-2 border-[#358ECD]/30 rounded-lg p-4">
          <h3 className="font-bold text-[#4D4D4D] text-base mb-3">Logger Diagnostics</h3>
          {loadingDiag ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><Loader className="w-4 h-4 animate-spin" /> Loading diagnostics…</div>
          ) : diagParams.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">No diagnostic parameters available.</p>
          ) : (
            <table className="w-full text-xs text-gray-700">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="text-left py-2 px-2 font-semibold">Status</th>
                  <th className="text-left py-2 px-2 font-semibold">Type</th>
                  <th className="text-left py-2 px-2 font-semibold">Value</th>
                  <th className="text-left py-2 px-2 font-semibold">Sample Time</th>
                </tr>
              </thead>
              <tbody>
                {diagParams.map((p, i) => {
                  const d = diag[p.id]
                  const has = d && d.value !== null && d.value !== undefined
                  const val = has ? (typeof d.value === 'number' ? Number(d.value.toFixed(2)) : d.value) : null
                  return (
                    <tr key={p.id || i} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 px-2"><span className={has ? 'text-green-600' : 'text-gray-300'}>●</span></td>
                      <td className="py-2 px-2">{p.name}{(d?.unit || p.unit) ? ` (${d?.unit || p.unit})` : ''}</td>
                      <td className="py-2 px-2 font-medium text-gray-900">{has ? val : '—'}</td>
                      <td className="py-2 px-2 text-gray-500">{d?.ts || '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
